// =============================================================================
// io/share/render.ts — showing a stranger a report we did not write.
// -----------------------------------------------------------------------------
// ── 🚨 THE PAYLOAD IS ATTACKER-CONTROLLABLE. THAT IS NOT THEORETICAL ──
//
// `shared_reports` is INSERT-able by any authenticated user for their own
// `user_id` — that is the whole RLS policy — so anyone who signs up can store
// arbitrary markup under a token of their choosing and send the link out.
//
// The old client used to write that markup into the live page with `innerHTML`.
// `innerHTML` does not run `<script>`, but it very much runs `<img onerror=…>`
// and `<svg onload=…>` — on the origin where a signed-in user's Supabase
// refresh token lives. D213 closed that.
//
// ▶ The close is a `sandbox` with NEITHER `allow-scripts` NOR
// `allow-same-origin`: the document is inert and sits in an opaque origin, so it
// cannot run code, cannot reach this page, and cannot read storage or cookies.
// Sanitising instead would be a deny-list against an attacker who picks the
// markup; this removes the capability rather than enumerating the abuses.
//
// ⚠ EVERY FUNCTION HERE IS WRITTEN TO KEEP THAT TRUE. In particular the
// payload is edited with `DOMParser` — a document with no browsing context,
// which runs no script and fetches no resource — never by assigning it to a live
// element to query it. Tidying up after a security fix must not undo it.
// =============================================================================

import type { ShareKind } from '../../domain/share';

/**
 * Every stylesheet rule this app is currently rendering with.
 *
 * ⚠ THE FRAME INHERITS NO STYLESHEET. A `srcdoc` document starts blank, so
 * without this the report renders as unstyled markup — which LOOKS like a broken
 * page rather than like a security control, and is how this kind of fix gets
 * reverted.
 *
 * ⚠ NOT `querySelectorAll('style')`, which is what the old client does and what
 * would silently return NOTHING here. That client is one HTML file with inline
 * `<style>` blocks; this one is BUILT, and its CSS is a `<link>` to a separate
 * file. `document.styleSheets` enumerates what the document is actually styled
 * by, rather than one way of expressing it.
 *
 * ── 🔴 AND NOT `cssText` EITHER, WHICH IS WHAT SHIPPED AND WAS WRONG ──
 *
 * `rule.cssText` serialises the CSSOM's PARSED form, and a shorthand that mixes
 * a literal with a `var()` does not survive that round trip: it is split into
 * longhands and every one comes back EMPTY.
 *
 * Measured on this app's own stylesheet rather than reasoned about — 8 of 2,295
 * rules were damaged, and the first is the one that matters most:
 *
 *     body { font: 14px var(--sans); }   →   font-style: ; font-family: ; …
 *
 * ▶ So every shared report rendered in TIMES NEW ROMAN while the signed-in app
 * beside it looked perfect. Josh reported it off a real link. The other seven
 * are `border-left: 3px solid var(--…)` accent bars, which simply vanished.
 *
 * ⚠ THE SOURCE TEXT HAS NO SUCH PROBLEM, so it is what travels. An inline
 * `<style>` carries its own; a `<link>` is fetched, same-origin, which
 * `connect-src 'self'` already allows. `cssText` survives only as a fallback,
 * where being eight rules wrong beats being entirely unstyled.
 *
 * ⚠ Every `url()` in this app's CSS is ROOT-relative (`url('/fonts/…')`), which
 * resolves against the frame's base — its parent — either way. A stylesheet
 * using `url(./x)` would need rebasing, and there is none.
 */
export async function collectAppCss(doc: Document = document): Promise<string> {
  const out: string[] = [];
  for (const sheet of Array.from(doc.styleSheets)) {
    const node = sheet.ownerNode as Element | null;
    // An inline <style> — the dev server's form, and the cheapest.
    if (node && node.nodeName === 'STYLE' && node.textContent) {
      out.push(node.textContent);
      continue;
    }
    if (sheet.href) {
      try {
        const res = await fetch(sheet.href, { credentials: 'omit' });
        if (res.ok) { out.push(await res.text()); continue; }
      } catch {
        // Cross-origin or offline — fall through to the lossy path.
      }
    }
    out.push(serializeSheet(sheet));
  }
  return out.join('\n');
}

/**
 * The last resort. ⚠ LOSSY — see the note above. It is here so a sheet whose
 * source cannot be read still contributes most of its rules, rather than none.
 *
 * ⚠ Its own try/catch: `cssRules` throws on a cross-origin sheet, and one such
 * sheet must not cost every rule after it.
 */
function serializeSheet(sheet: CSSStyleSheet): string {
  try {
    return Array.from(sheet.cssRules).map((r) => r.cssText).join('\n');
  } catch {
    return '';
  }
}

/**
 * Remove a captured trades table from a stored payload.
 *
 * ── ⚠ WHY THIS RUNS ON THE WAY OUT AS WELL AS THE WAY IN ──
 *
 * A share created before the capture started stripping it has the old markup
 * frozen in the database, and nothing can edit that row. Rendering our own
 * clickable table underneath it produced TWO tables, one of them dead. So the
 * same removal runs here: old links heal, new links are belt-and-braces, and
 * there is no version of a payload that produces two.
 *
 * ⚠ ONLY WHEN WE ARE REPLACING IT. A shared DASHBOARD carries no trades, so
 * stripping unconditionally would delete its Recent Trades table and put nothing
 * back in its place.
 */
export function stripCapturedTable(html: string, replacing: boolean): string {
  if (!replacing) return html;
  try {
    const doc = new DOMParser().parseFromString(`<div id="__sr">${html}</div>`, 'text/html');
    const root = doc.getElementById('__sr');
    if (!root) return html;
    // `.share-trades-table` is the marker both clients now write;
    // `table.trades` catches captures made before it existed.
    root.querySelectorAll('.share-trades-table, table.trades').forEach((n) => {
      const card = n.closest('.card') ?? n;
      const prev = card.previousElementSibling;
      if (prev?.classList?.contains('an-section-divider')) prev.remove();
      card.remove();
    });
    // And the column Prop Rails left behind. Newer captures carry a class; older
    // ones only the inline style, so both are matched — otherwise every link
    // already sent keeps its chart at two-thirds width beside a hole.
    root.querySelectorAll('.dayview-intraday, .dv-intraday-grid, [style*="grid-template-columns:2fr 1fr"]')
      .forEach((g) => {
        if (g.children.length <= 1) (g as HTMLElement).style.gridTemplateColumns = '1fr';
      });
    return root.innerHTML;
  } catch {
    // A payload we cannot parse is rendered exactly as it was stored.
    return html;
  }
}

/**
 * The document the frame is given.
 *
 * ⚠ NO `<base>` TAG, and that is a change from the old client rather than an
 * omission. A `srcdoc` document takes its base URL from its PARENT by spec, so
 * the tag buys nothing — and the policy this frame inherits refuses it anyway,
 * so it would log an error on a page whose whole job is to look correct to a
 * stranger.
 *
 * ⚠ THAT POLICY IS `base-uri 'self'` NOW, NOT `'none'` (TCR-65), AND THIS FRAME
 * IS NOT LOOSENED BY IT. The frame is sandboxed with neither `allow-scripts` nor
 * `allow-same-origin`, so it runs in an OPAQUE origin — and a `'self'` source
 * expression cannot match our hostname there. `public/_headers` records that as
 * measured, and it is why `font-src` and `img-src` spell our own origins out by
 * name instead of leaning on `'self'`. Inside this frame `'self'` is therefore
 * still effectively `'none'`, so the reason for shipping no `<base>` got
 * STRONGER, not weaker.
 *
 * ⚠ `noindex` because a shared link is not a public page. It is unlisted, and a
 * search engine that found one in a referrer log must not publish it further.
 */
export function buildFrameSrcdoc(
  { css, kind, html }: { css: string; kind: ShareKind | null; html: string },
): string {
  return '<!doctype html><html><head><meta charset="utf-8">'
    + '<meta name="robots" content="noindex,noarchive,nosnippet">'
    + `<style>${css}</style>`
    // ⚠ 8px of trailing padding, not 40. The visitor's own trades table sits
    // directly beneath this frame, so padding inside it reads as a gap between
    // the report and the table rather than as the end of a page.
    + '<style>body{margin:0;padding:0 24px 8px;background:transparent;}</style>'
    + '</head><body>'
    + `<div class="tab-section" data-tab="${kind ?? ''}">${html}</div>`
    + '</body></html>';
}

/**
 * How tall that document actually is.
 *
 * ── ⚠ THE VISIBLE FRAME CANNOT BE MEASURED, BY DESIGN ──
 *
 * It has no `allow-same-origin`, so this page cannot read into it. That is the
 * security control working. Both obvious workarounds are wrong: `allow-scripts`
 * would hand the attacker-authored payload script execution, and parking the
 * HTML in THIS document to measure it would execute `<img onerror=…>` on our own
 * origin — the exact bug the sandbox closed.
 *
 * ▶ So measurement happens in a SECOND frame: hidden, discarded immediately, and
 * granted `allow-same-origin` AND NOTHING ELSE. Without `allow-scripts` no code
 * in it can run, so the same-origin grant has no actor — it exists only so this
 * page can read a number off an inert document. The two tokens never appear on
 * the same element, which is the pairing that would be dangerous.
 */
export function measureSrcdocHeight(
  srcdoc: string,
  width: number,
  timeoutMs = 4000,
): Promise<number> {
  return new Promise((resolve) => {
    let done = false;
    const f = document.createElement('iframe');
    // ⚠ `allow-same-origin` ALONE, and never beside `allow-scripts`.
    f.setAttribute('sandbox', 'allow-same-origin');
    f.setAttribute('aria-hidden', 'true');
    f.style.cssText =
      `position:absolute;left:-99999px;top:0;width:${width}px;height:10px;border:0;visibility:hidden;`;

    const finish = (h: number) => {
      if (done) return;
      done = true;
      try { f.remove(); } catch { /* already gone */ }
      resolve(h > 0 ? h : 0);
    };

    f.onload = () => {
      try {
        const d = f.contentDocument;
        finish(Math.max(
          d?.body?.scrollHeight ?? 0,
          d?.documentElement?.scrollHeight ?? 0,
        ));
      } catch {
        finish(0);
      }
    };
    // ⚠ A payload that never fires `load` must not leave the report waiting
    // forever on a measurement it can do without.
    setTimeout(() => finish(0), timeoutMs);

    // ── ⚠ SRCDOC BEFORE APPEND, AND THAT ORDER IS THE WHOLE FUNCTION ──
    //
    // Appending an iframe with no content loads `about:blank` and fires `load`
    // IMMEDIATELY, on an empty document. Assigning srcdoc afterwards means the
    // handler has already run and resolved 0, and the real document is never
    // measured — which looks exactly like a measurement that failed silently.
    f.srcdoc = srcdoc;
    document.body.appendChild(f);
  });
}
